import gsap from 'gsap';
import { PixiBase } from '../../core/pixi/PixiBase';

import { isMobile } from '../../helpers/isMobile';
import { isoStandard } from '../../helpers/isoStandard';
import { exchangeAmount } from '../../helpers/exchangeAmountByRate';
import { parseNumberPrecisionByDoteCount } from '../../helpers/parseNumberByPrecision';

import { userInfoStore } from '../../store/userInfoStore';

import { containerNames } from '../../constants';
import { gameMainDrawer } from './gameMainDrawer';
import { gameStore } from '../../store/gameStore';
import { dateHelper } from '../../helpers/gameDate';

const JackpotConfig = {
  clubAmount: {
    id: 1,
    sprite: 'JackpotClubs',
    textKey: 'clubAmount',
    personIcon: 'personIcon',
    jackpotInfoPopupSprite: 'jackPotInfoPopup',
    popupPosition: isMobile() ? 3 : 28,
    textPosition: isMobile() ? 28 : 87,
    spritePosition: isMobile() ? 0 : 43,
    secretIdPosition: isMobile() ? 110 : 225,
  },
  diamondAmount: {
    id: 2,
    sprite: 'JackpotDiamond',
    textKey: 'diamondAmount',
    personIcon: 'personIcon',
    jackpotInfoPopupSprite: 'jackPotInfoPopup',
    popupPosition: isMobile() ? 108 : 292,
    textPosition: isMobile() ? 151 : 348,
    spritePosition: isMobile() ? 120 : 307,
    secretIdPosition: isMobile() ? 230 : 490,
  },
  heartAmount: {
    id: 3,
    sprite: 'JackpotHeart',
    textKey: 'heartAmount',
    personIcon: 'personIcon',
    jackpotInfoPopupSprite: 'jackPotInfoPopup',
    popupPosition: isMobile() ? 227 : 947,
    textPosition: isMobile() ? 270 : 1003,
    spritePosition: isMobile() ? 239 : 960,
    secretIdPosition: isMobile() ? 380 : 1142,
  },
  spadeAmount: {
    id: 4,
    sprite: 'jackpotSpade',
    textKey: 'spadeAmount',
    personIcon: 'personIcon',
    jackpotInfoPopupSprite: 'jackPotInfoPopup',
    popupPosition: isMobile() ? 376 : 1194,
    textPosition: isMobile() ? 419 : 1253,
    spritePosition: isMobile() ? 389 : 1207,
    secretIdPosition: isMobile() ? 530 : 1390,
  },
};

const infoPopupDataEnum = [
  {
    y: isMobile() ? 10 : 15,
    title: gameStore.canvasTranslationKeys.LARGEST_WINNER,
    key: 'LARGEST_WINNER',
    name: 'LargestWinnerContainer',
    children: [
      {
        key: 'maxWinGameName',
        x: 10,
        y: isMobile() ? 23 : 32,
        fontSize: 10,
        anchor: 0,
        isText: true,
      },
      {
        key: 'maxWin',
        x: isMobile() ? 190 : 257,
        y: isMobile() ? 38 : 46,
        fontSize: isMobile() ? 12 : 14,
        anchor: 1,
      },
      {
        key: 'maxWinDate',
        x: 10,
        y: isMobile() ? 38 : 46,
        fontSize: isMobile() ? 10 : 12,
        anchor: 0,
        isDate: true,
      },
    ],
  },
  {
    y: isMobile() ? 60 : 72,
    title: gameStore.canvasTranslationKeys.LAST_WINNER,
    key: 'LAST_WINNER',
    name: 'LastWinnerContainer',
    children: [
      {
        key: 'lastWinGameName',
        x: 10,
        y: isMobile() ? 73 : 87,
        fontSize: 10,
        anchor: 0,
        isText: true,
      },
      {
        key: 'lastWin',
        x: isMobile() ? 190 : 257,
        y: isMobile() ? 87 : 102,
        fontSize: isMobile() ? 12 : 14,
        anchor: 1,
      },
      {
        key: 'lastWinDate',
        x: 10,
        y: isMobile() ? 87 : 102,
        fontSize: isMobile() ? 10 : 12,
        anchor: 0,
        isDate: true,
      },
    ],
  },
  {
    y: isMobile() ? 110 : 130,
    title: gameStore.canvasTranslationKeys.NUMBER_OF_WINNERS,
    key: 'NUMBER_OF_WINNERS',
    children: [
      {
        key: 'totalWin',
        x: isMobile() ? 190 : 257,
        y: isMobile() ? 136 : 160,
        fontSize: isMobile() ? 12 : 14,
        anchor: 1,
      },
      {
        key: 'winsCount',
        x: isMobile() ? 31 : 41,
        y: isMobile() ? 137 : 160,
        fontSize: isMobile() ? 10 : 12,
        anchor: 0,
        isCount: true,
      },
    ],
    name: 'NumberOfWinnersWinnerContainer',
  },
];

export class GameJackpotDrawer {
  constructor() {
    this.GameJackpotContainer = PixiBase.createContainer(containerNames.GameJackpotContainer);
    this.inputWithTextContainer = PixiBase.createContainer('inputWithTextContainer');
    this.jackpotAmountInputContainer = PixiBase.createContainer(
      containerNames.JackpotAmountInputContainer
    );
    this.jackpotInfoPopupContainer = PixiBase.createContainer(
      containerNames.JackpotInfoPopupContainer
    );
    this.sprites = [];
    this.textElements = {};
    this.jackpotAmounts = {};
    this.currentPopup = null;
    this.winnerInfo = {};
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.popupInfoTexturesById = {};
    this.secretIdsArr = [];
    this.elementLength = 0;
  }

  setupJackpotAmountInputs() {
    Object.keys(JackpotConfig).forEach((amountKey) => {
      const config = JackpotConfig[amountKey];
      const spriteKey = config.sprite;
      const spriteTexture = this.spriteSheet[spriteKey];
      const x = config.spritePosition;
      const amount = this.jackpotAmounts[amountKey];
      const secretId = PixiBase.createText('', {
        fontSize: isMobile() ? 15 : 18,
        fill: '#FFFFFF',
        fontWeight: 600,
      });
      secretId.anchor.x = 1;
      secretId.id = config.id;
      secretId.visible = false;
      this.secretIdsArr.push(secretId);
      PixiBase.setPosition(secretId, { x: config.secretIdPosition, y: isMobile() ? 0 : 25 });
      const textValue = `${
        isoStandard(+parseNumberPrecisionByDoteCount(exchangeAmount(amount || 0))) || '0'
      }`;

      this.textElements[config.textKey] = PixiBase.createText(textValue, {
        fontSize: isMobile() ? 15 : this.elementLength > 14 ? 16 : 18,
        fill: 'white',
        fontWeight: 600,
      });
      this.textElements[config.textKey].anchor.set(0, 0.5);
      PixiBase.setPosition(this.textElements[config.textKey], {
        x: config.textPosition,
        y: isMobile() ? 22 : 54,
      });
      if (spriteTexture) {
        const inputWithTextItemContainer = PixiBase.createContainer('inputWithTextItemContainer');
        inputWithTextItemContainer.children = [];
        const sprite = PixiBase.createSprite(spriteTexture);
        sprite.id = config.id;
        inputWithTextItemContainer.id = config.id;
        PixiBase.setPosition(sprite, { x, y: isMobile() ? 6 : 35 });
        PixiBase.setupButtonMode(inputWithTextItemContainer, {
          interactive: true,
          buttonMode: true,
        });
        inputWithTextItemContainer.addChild(sprite);
        inputWithTextItemContainer.addChild(this.textElements[config.textKey]);
        inputWithTextItemContainer.addChild(secretId);
        this.inputWithTextContainer.addChild(inputWithTextItemContainer);

        this.sprites.push(sprite);

        if (isMobile()) {
          PixiBase.registerClick(inputWithTextItemContainer, (e) => {
            this.handleSpriteClick(e);
          });
        } else {
          PixiBase.registerPointerover(inputWithTextItemContainer, (e) => {
            if (this.winnerInfo[e.target.id]) {
              this.jackpotInfoPopupContainer.children[e.target.id - 1].visible = true;
              this.handleInfoPopupTextById(e.target.id);
            }
          });
          PixiBase.registerPointerout(inputWithTextItemContainer, () => {
            this.jackpotInfoPopupContainer.children.forEach((el) => {
              el.visible = false;
            });
          });
        }
        this.sprite = sprite;
      }
    });
    this.GameJackpotContainer.addChild(this.inputWithTextContainer);
  }

  handleJackpotWinData({ secretId, jackpotCard }) {
    this.secretIdsArr.forEach(({ id }) => {
      if (secretId && id === jackpotCard) {
        this.secretIdsArr[id - 1].visible = true;
        this.secretIdsArr[id - 1].text = secretId;
        gsap
          .timeline({
            repeat: 3,
            onComplete: () => {
              this.secretIdsArr[id - 1].visible = false;
            },
          })
          .to(this.secretIdsArr[id - 1], { alpha: 1, duration: 1 })
          .to(this.secretIdsArr[id - 1], { alpha: 0, duration: 1 });
      }
    });
  }

  updateJackpotAmounts(newAmounts) {
    const existingKeys = Object.keys(JackpotConfig);
    Object.keys(newAmounts).forEach((amountKey) => {
      if (existingKeys.includes(amountKey)) {
        const element = this.textElements[amountKey];
        if (element) {
          const newAmount = exchangeAmount(newAmounts[amountKey]);
          element.style.fontSize = isMobile()
            ? 15
            : `${isoStandard(newAmount)}`.length > 14
            ? 16
            : 18;
          const isInteger = newAmount === Math.floor(newAmount);
          gsap.to(element, {
            textContent: newAmount || 0,
            duration: 0.8,
            ease: 'none',
            snap: { textContent: isInteger ? 1 : 1 / 10 ** userInfoStore.precision },
            stagger: {
              each: 1.0,
              onUpdate: function () {
                element.text = isoStandard(
                  +parseNumberPrecisionByDoteCount(
                    +this.targets()[0].textContent,
                    1,
                    isInteger ? 0 : userInfoStore.precision
                  )
                );
              },
              onComplete: function () {
                element.text = isoStandard(
                  +parseNumberPrecisionByDoteCount(
                    +this.targets()[0].textContent,
                    1,
                    isInteger ? 0 : userInfoStore.precision
                  )
                );
              },
            },
          });
        }
      }
    });
  }

  handleSpriteClick(e) {
    e.stopPropagation();
    this.jackpotInfoPopupContainer.children.forEach((el, index) => {
      if (this.winnerInfo[e.target.id]) {
        el.visible = index === e.target.id - 1 && !el.visible;
      }
    });
    const isOpenedPopup = this.jackpotInfoPopupContainer.children.some((el) => el.visible);
    const { stage } = gameMainDrawer.pixiApplication;
    if (isOpenedPopup) {
      stage.interactive = true;
      this.handleInfoPopupTextById(e.target.id);
      stage.on('pointerdown', this.handleOutsideClick);
    } else {
      stage.interactive = false;
      stage.off('pointerdown', this.handleOutsideClick);
    }
  }

  handleJackpotWinnerInfo(data) {
    this.winnerInfo = data.reduce((acc, current) => {
      acc[current.jackpotCard] = current;
      return acc;
    }, {});
  }

  setupJackpotInfoPopup() {
    const configArr = Object.keys(JackpotConfig);
    configArr.forEach((amountKey, i) => {
      const config = JackpotConfig[amountKey];
      const personSpriteKey = config.personIcon;
      const popupSpriteKey = config.jackpotInfoPopupSprite;
      const popupSpriteTexture = this.spriteSheet[popupSpriteKey];
      this.popupSpriteTexture = popupSpriteTexture;
      const personSpriteTexture = this.spriteSheet[personSpriteKey];
      const x = config.popupPosition;

      if (popupSpriteTexture) {
        const infoPopupWithTextContainer = PixiBase.createContainer('infoPopupWithTextContainer');
        const popupSprite = PixiBase.createSprite(popupSpriteTexture);
        const personSprite = PixiBase.createSprite(personSpriteTexture);
        infoPopupWithTextContainer.visible = false;
        PixiBase.setPosition(personSprite, { x: isMobile() ? 7 : 10, y: isMobile() ? 136 : 158 });
        PixiBase.setPosition(infoPopupWithTextContainer, { x, y: isMobile() ? 42 : 80 });
        infoPopupWithTextContainer.addChild(popupSprite);
        infoPopupWithTextContainer.addChild(personSprite);
        const textContainer = PixiBase.createContainer('textContainer');
        infoPopupDataEnum.forEach((item) => {
          const titleText = PixiBase.createText(gameStore.canvasTranslationKeys[item.key], {
            fontWeight: 500,
            fill: '#FFC33E',
            fontSize: isMobile() ? 10 : 12,
            resolution: 2,
            fontFamily: 'Arial',
          });
          titleText.x = 10;
          titleText.y = item.y;
          textContainer.addChild(titleText);
          item.children.forEach((el) => {
            const text = PixiBase.createText('', {
              fontWeight: 500,
              fill: '#FFFFFF',
              fontSize: el.fontSize,
              resolution: 2,
              fontFamily: 'Arial',
            });
            if (el.isDate) text.alpha = 0.6;
            text.x = el.x;
            text.y = el.y;
            text.key = el.key;
            text.isDate = el.isDate;
            text.isText = el.isText;
            text.isCount = el.isCount;
            text.anchor.x = el.anchor;
            textContainer.addChild(text);
            infoPopupWithTextContainer.addChild(textContainer);
          });
        });
        this.jackpotInfoPopupContainer.addChild(infoPopupWithTextContainer);
        if (i === configArr.length - 1) {
          this.handleInfoPopupLastItemPosition(infoPopupWithTextContainer);
        }
      }
    });
    this.GameJackpotContainer.addChild(this.jackpotInfoPopupContainer);
    this.setupJackpotAmountInputs();
  }

  handleInfoPopupLastItemPosition(container) {
    container.children.forEach((item, index) => {
      if (!index) {
        item.scale.x = -1;
        item.x = isMobile() ? 161 : 220;
      } else if (index === 1) {
        item.x = isMobile() ? -27 : -40;
      } else {
        item.x = isMobile() ? -35 : -50;
      }
    });
  }

  handleOutsideClick(e) {
    const openedPopup = this.jackpotInfoPopupContainer.children.find((el) => el.visible);
    if (!openedPopup) {
      return;
    }
    const bounds = openedPopup.getBounds();
    const clickX = e.globalX;
    const clickY = e.globalY;
    const isOutside =
      clickX < bounds.x ||
      clickX > bounds.x + bounds.width ||
      clickY < bounds.y ||
      clickY > bounds.y + bounds.height;
    if (isOutside) {
      const { stage } = gameMainDrawer.pixiApplication;
      this.jackpotInfoPopupContainer.children.forEach((el) => {
        el.visible = false;
      });
      stage.interactive = false;
      stage.off('pointerdown', this.handleOutsideClick);
    }
  }

  handleInfoPopupTextById(id) {
    const winners = Object.values(this.winnerInfo);
    if (!winners.length) {
      return;
    }
    const amountsInfo = winners.find((el) => el.jackpotCard === id);
    if (!amountsInfo) {
      return;
    }
    this.jackpotInfoPopupContainer.children[id - 1].children[2].children.forEach((el) => {
      if (el.key) {
        el.text = el.isDate
          ? dateHelper(amountsInfo[el.key])
          : el.isText
          ? amountsInfo[el.key]
          : el.isCount
          ? `${isoStandard(+amountsInfo[el.key])}`
          : `${isoStandard(
              +parseNumberPrecisionByDoteCount(exchangeAmount(amountsInfo[el.key]), 1)
            )}`;
      }
    });
  }

  execute(config, spriteSheet) {
    const [gameImageSpriteSheet] = spriteSheet;
    this.config = config;
    this.spriteSheet = gameImageSpriteSheet;
  }
}
